import { useEffect, useState } from "react";
import { api, type AuthUser, type User, type UserRole } from "../lib/api";

export function SettingsUsers(props: { currentUser: AuthUser }) {
  const [list, setList] = useState<User[]>([]);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>("user");
  const [inviteResult, setInviteResult] = useState<{ email: string; password: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ email: "", display_name: "", role: "user" as UserRole });
  const [editErr, setEditErr] = useState<string | null>(null);

  const load = async () => setList(await api.users.list());

  useEffect(() => {
    load();
  }, []);

  const invite = async () => {
    setBusy(true);
    setErr(null);
    setInviteResult(null);
    try {
      const res = await api.users.invite(inviteEmail, inviteName, inviteRole);
      setInviteResult({ email: res.user.email, password: res.temp_password });
      setInviteEmail("");
      setInviteName("");
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (u: User) => {
    setEditingId(u.id);
    setEditForm({ email: u.email, display_name: u.display_name, role: u.role });
    setEditErr(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setBusy(true);
    setEditErr(null);
    try {
      await api.users.patch(editingId, {
        email: editForm.email,
        display_name: editForm.display_name,
        role: editForm.role,
      });
      setEditingId(null);
      await load();
    } catch (e: any) {
      setEditErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (u: User) => {
    if (!confirm(`Benutzer "${u.display_name}" (${u.email}) wirklich löschen?`)) return;
    setBusy(true);
    try {
      await api.users.delete(u.id);
      await load();
    } catch (e: any) {
      alert(`Löschen fehlgeschlagen: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h3>Benutzer einladen</h3>
      <div className="invite-form">
        <input type="email" placeholder="email@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} disabled={busy} />
        <input type="text" placeholder="Anzeigename" value={inviteName} onChange={(e) => setInviteName(e.target.value)} disabled={busy} />
        <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as UserRole)} disabled={busy}>
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="developer">Developer</option>
        </select>
        <button className="primary" disabled={busy || !inviteEmail || !inviteName} onClick={invite}>Einladen</button>
      </div>

      {err && <div className="error">{err}</div>}
      {inviteResult && (
        <div className="invite-result">
          <strong>Erfolgreich eingeladen:</strong> <code>{inviteResult.email}</code>
          <br />
          <strong>Temporäres Passwort (nur einmal angezeigt):</strong>
          <code className="password-reveal">{inviteResult.password}</code>
          <p className="muted">
            Kopiere das Passwort und schicke es out-of-band (Signal). Der User muss es beim ersten Login ändern.
          </p>
        </div>
      )}

      <h3>Benutzerliste</h3>
      <table className="data-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Rolle</th>
            <th>Erstellt</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {list.map((u) => {
            const isMe = u.id === props.currentUser.id;
            const isEditing = editingId === u.id;
            return (
              <tr key={u.id}>
                {isEditing ? (
                  <>
                    <td>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.display_name}
                        onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                      />
                    </td>
                    <td>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value as UserRole })}
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                        <option value="developer">developer</option>
                      </select>
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString("de-DE")}</td>
                    <td>
                      <button className="link" disabled={busy} onClick={saveEdit}>Speichern</button>
                      {" · "}
                      <button className="link" onClick={() => setEditingId(null)}>Abbrechen</button>
                      {editErr && <div className="error">{editErr}</div>}
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      {u.email}
                      {isMe && <span className="muted"> (du)</span>}
                    </td>
                    <td>{u.display_name}</td>
                    <td>{u.role}</td>
                    <td>{new Date(u.created_at).toLocaleDateString("de-DE")}</td>
                    <td>
                      <button className="link" onClick={() => startEdit(u)}>Ändern</button>
                      {!isMe && (
                        <>
                          {" · "}
                          <button className="link" onClick={() => del(u)} disabled={busy}>
                            Löschen
                          </button>
                        </>
                      )}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
